import { baseTheme } from './base';
import {
  PERSISTED_COLOUR_ROLES,
  type PersistedColourName,
  persistedColours,
} from './persisted';
import { printTheme } from './print';
import {
  blueWhiteCssVariablesV2,
  darkCssVariablesV2,
  isThemeName,
  lightCssVariablesV2,
  THEME_NAMES,
  type ThemeName,
  themesV2,
} from './v2';

export {
  baseTheme,
  PERSISTED_COLOUR_ROLES,
  type PersistedColourName,
  printTheme,
};

export type ThemeMode = 'light' | 'dark';

/** Colour scheme of each theme's table. */
const THEME_MODES: Readonly<Record<ThemeName, ThemeMode>> = {
  light: 'light',
  dark: 'dark',
  'blue-white': 'dark',
};

/** Colour scheme of the table behind `name`; unknown names render light. */
export const themeModeOf = (name: string | undefined): ThemeMode =>
  isThemeName(name) ? THEME_MODES[name] : 'light';

/** `:is(...)` selector for every `data-theme` whose table has `mode`. */
export const themeModeSelector = (mode: ThemeMode): string =>
  `:is(${THEME_NAMES.filter(name => themeModeOf(name) === mode)
    .map(name => `[data-theme='${name}']`)
    .join(', ')})`;

export type ColourIsland = 'document' | 'frame';

/** `:is(...)` selector for every `data-theme` whose document table has `mode`. */
export const documentModeSelector = (mode: ThemeMode): string =>
  `:is(${THEME_NAMES.filter(name => themeModeOf(documentThemes[name]) === mode)
    .map(name => `[data-theme='${name}']`)
    .join(', ')})`;

/**
 * Selector for `kind` colour islands under `name`. A document island keeps
 * nested elements that repeat the theme's `data-theme` on its table. A frame
 * island is app chrome: it takes the app's (root) table even inside an editor
 * that renders another theme.
 */
export const colourIslandSelector = (
  name: ThemeName,
  kind: ColourIsland
): string =>
  kind === 'frame'
    ? `:root[data-theme='${name}'] [data-colour-island='frame']`
    : `[data-theme='${name}'] [data-colour-island='document'], [data-theme='${name}'] [data-colour-island='document'] [data-theme='${name}']`;

/**
 * Table each theme uses on document surfaces (`data-colour-island="document"`).
 * Blue-white frames light documents; light and dark keep their own table.
 */
export const documentThemes: Readonly<Record<ThemeName, ThemeName>> = {
  light: 'light',
  dark: 'dark',
  'blue-white': 'light',
};

export type Kebab<
  T extends string,
  A extends string = '',
> = T extends `${infer F}${infer R}`
  ? Kebab<R, `${A}${F extends Lowercase<F> ? '' : '-'}${Lowercase<F>}`>
  : A;

export type AffineTheme = typeof baseTheme &
  Record<PersistedColourName, string> & {
    editorMode: 'page' | 'edgeless';
  };

export type AffineCssVariables = {
  [Key in `--streamlain-${Kebab<keyof AffineTheme>}`]: string;
};

export const camelToKebab = (s: string): string =>
  typeof s !== 'string'
    ? ''
    : s
        .replace(/-/g, '_')
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/([A-Z])([A-Z])(?=[a-z])/g, '$1-$2')
        .toLowerCase()
        .replace(/(\D+)(\d+)$/g, '$1-$2')
        .replace(/\s|_/g, '-');

export const themeToVar = (themeName: keyof AffineTheme) =>
  `--streamlain-${camelToKebab(themeName)}` as `--streamlain-${Kebab<keyof AffineTheme>}`;

function toCssVariables(theme: Record<string, string>): AffineCssVariables {
  return Object.fromEntries(
    Object.entries(theme).map(([name, value]) => [
      themeToVar(name as keyof AffineTheme),
      value,
    ])
  ) as AffineCssVariables;
}

/** Stored colour names of one theme, as variables. */
const persistedVariables = (name: ThemeName) =>
  toCssVariables(persistedColours(themesV2[name]));

/** Variables outside the v2 roles: shared values plus stored colour names. */
export const lightCssVariables = {
  ...toCssVariables(baseTheme),
  ...persistedVariables('light'),
};
export const darkCssVariables = {
  ...toCssVariables(baseTheme),
  ...persistedVariables('dark'),
};
export const printCssVariables = toCssVariables(printTheme);

/** Every theme variable, for the default (`:root`) scope. */
export const combinedLightCssVariables = {
  ...lightCssVariables,
  ...lightCssVariablesV2,
};
export const combinedDarkCssVariables = {
  ...darkCssVariables,
  ...darkCssVariablesV2,
};

/** Only the colour variables that differ between themes, for `[data-theme]` scopes. */
export const scopedCssVariables: Readonly<
  Record<ThemeName, Record<string, string>>
> = {
  light: { ...persistedVariables('light'), ...lightCssVariablesV2 },
  dark: { ...persistedVariables('dark'), ...darkCssVariablesV2 },
  'blue-white': {
    ...persistedVariables('blue-white'),
    ...blueWhiteCssVariablesV2,
  },
};

export function cssVar(
  key: keyof AffineCssVariables | keyof AffineTheme,
  fallback?: string
): string {
  const name = key.startsWith('--')
    ? key
    : themeToVar(key as keyof AffineTheme);
  return `var(${name}${fallback ? `, ${fallback}` : ''})`;
}
